import { forwardRef, useEffect, useImperativeHandle } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type FieldErrors } from "react-hook-form";

import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField } from "../form/FormField";
import { useTemplateStore } from "@/store/templates.store";
import { useAuthStore } from "@/store/auth.store";
import {
  RecorderFormSchema,
  type RecorderFormSchemaType,
} from "@/schemas/newVisit.schema";
import { DefaultRecordingTemplates } from "@/common/enums/enums";
import { DEFAULT_TEMPLATE_KEY } from "@/lib/onboarding";
import { toast } from "@/hooks/use-toast";
import { t } from "i18next";

export interface RecorderFormHandle {
  submitForm: () => Promise<void>;
}

export interface RecorderFormProps {
  onSubmitData?: (data: RecorderFormSchemaType) => Promise<void>;
}

const RecorderForm = forwardRef<RecorderFormHandle, RecorderFormProps>(
  ({ onSubmitData }, ref) => {
    const { getUser, user, userData } = useAuthStore.getState();
    const { fetchAndSetTemplates, templates } = useTemplateStore();

    useEffect(() => {
      if (user?.email && !userData) {
        void getUser(user.email);
      }

      if (userData?.id) {
        void fetchAndSetTemplates({ userId: userData.id });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const defaultTemplateValue = String(
      localStorage.getItem(DEFAULT_TEMPLATE_KEY) ??
        templates.find((template) => template.isDefault === 1)?.id ??
        "SOAP Template"
    );

    const formMethods = useForm<RecorderFormSchemaType>({
      resolver: zodResolver(RecorderFormSchema),
      defaultValues: {
        template: defaultTemplateValue,
        style: userData?.style || "Bullet Point",
        format: userData?.format || "Concise",
        examination: "Physical",
        templateName: defaultTemplateValue,
        customTemplateId: null,
      },
    });

    const defaultTemplates: string[] = Object.values(DefaultRecordingTemplates);
    const styleOptions: string[] = ["Paragraph", "Bullet Point"];
    const formatOptions: string[] = ["Concise", "Detailed"];
    const examinationOptions: string[] = ["Physical", "Virtual"];
    const selectedTemplate = formMethods.watch("template");
    const isCustomTemplate = !defaultTemplates.includes(selectedTemplate);
    const isVoiceToTextTemplate =
      selectedTemplate === DefaultRecordingTemplates.VOICE_TO_TEXT.toString();

    useEffect(() => {
      const currentTemplate = formMethods.getValues("template");
      if (currentTemplate !== defaultTemplateValue) {
        formMethods.setValue("template", defaultTemplateValue);
      }
    }, [defaultTemplateValue, formMethods]);

    const customTemplates: { id: string; name: string }[] =
      templates?.map((tpl) => ({
        id: String(tpl.id),
        name: tpl.name,
      })) || [];

    const showValidationErrors = (
      errors: FieldErrors<RecorderFormSchemaType>
    ): void => {
      const firstError = Object.values(errors)[0];
      if (firstError?.message) {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: String(firstError.message),
        });
      }
    };

    async function onSubmit(
      formData: RecorderFormSchemaType
    ): Promise<RecorderFormSchemaType> {
      if (onSubmitData) {
        const template = formData.template;
        const selectedCustom = customTemplates.find(
          (tpl) => tpl.id === template
        );
        if (isVoiceToTextTemplate || isCustomTemplate) {
          formData.style = "";
          formData.format = "";
          formData.examination = "";
        }
        if (selectedCustom) {
          formData.templateName = "Custom";
          formData.customTemplateId = Number(selectedCustom.id);
        } else {
          formData.templateName = template;
          formData.customTemplateId = null;
        }
        await onSubmitData(formData);
      }
      return formData;
    }

    useImperativeHandle(ref, () => ({
      submitForm: async (): Promise<void> => {
        await formMethods.handleSubmit(onSubmit, showValidationErrors)();
      },
    }));

    return (
      <Form {...formMethods}>
        <form
          className="flex flex-col gap-6 w-full"
          onSubmit={formMethods.handleSubmit(onSubmit, showValidationErrors)}
        >
          <div className="flex flex-col gap-4">
            <FormField
              control={formMethods.control}
              name="template"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("components.recorderForm.templateLabel")}
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(selectedValue: string) => {
                        field.onChange(selectedValue);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>
                            {t(
                              "components.recorderForm.defaultTemplatesSelectLabel"
                            )}
                          </SelectLabel>
                          {defaultTemplates.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        {customTemplates.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>
                              {t(
                                "components.recorderForm.customTemplatesSelectLabel"
                              )}
                            </SelectLabel>
                            {customTemplates.map((customTpl) => (
                              <SelectItem
                                key={customTpl.id}
                                value={customTpl.id}
                              >
                                {customTpl.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Voice to text template and custom templates doesn't support any more options */}
            {!isVoiceToTextTemplate && !isCustomTemplate && (
              <>
                <FormField
                  control={formMethods.control}
                  name="style"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.styleLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Style" />
                          </SelectTrigger>
                          <SelectContent>
                            {styleOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formMethods.control}
                  name="format"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.formatLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Format" />
                          </SelectTrigger>
                          <SelectContent>
                            {formatOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formMethods.control}
                  name="examination"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.examinationLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Examination" />
                          </SelectTrigger>
                          <SelectContent>
                            {examinationOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
          </div>
        </form>
      </Form>
    );
  }
);

RecorderForm.displayName = "RecorderForm";
export default RecorderForm;
